<template>
  <div class="live-trend">
    <div class="trend-header">
      <span class="trend-title">{{ title }}</span>
      <div class="trend-select" v-if="rangeText">
        <span>{{ rangeText }}</span>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
          <path d="M7 10L0 0H14L7 10Z" fill="rgba(209, 222, 255, 0.75)" />
        </svg>
      </div>
    </div>
    <div class="trend-body">
      <div class="legend-title" v-if="metricTitle">
        <span>{{ metricTitle }}</span>
        <span class="help-icon" v-if="showHelp">?</span>
      </div>
      <div ref="chartContainer" class="chart-container"></div>
    </div>
  </div>
</template>

<script>
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import * as echarts from "echarts";

const defaultXAxisData = ["19:06", "20:44", "22:11", "23:38", ""];
const defaultSeriesData = [
  { name: "在线", data: [18, 56, 172, 330, 405], color: "#25dfff" },
  { name: "进场", data: [24, 78, 292, 620, 742], color: "#2478ff" },
  { name: "离场", data: [8, 28, 82, 136, 190], color: "#c7d0e4", lineWidth: 5 },
];

function hexToRgba(color, alpha) {
  if (!color || color.indexOf("#") !== 0) return color || "rgba(255,255,255,0)";

  const hex = color.replace("#", "");
  const normalized = hex.length === 3
    ? hex.split("").map((item) => item + item).join("")
    : hex;
  const value = parseInt(normalized, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export default {
  name: "LiveTrend",
  props: {
    title: {
      type: String,
      default: "实时趋势",
    },
    metricTitle: {
      type: String,
      default: "在线人数",
    },
    rangeText: {
      type: String,
      default: "近2小时",
    },
    showHelp: {
      type: Boolean,
      default: true,
    },
    xAxisData: {
      type: Array,
      default: () => defaultXAxisData,
    },
    seriesData: {
      type: Array,
      default: () => defaultSeriesData,
    },
    yAxis: {
      type: Object,
      default: () => ({
        min: 0,
        max: 800,
        interval: 200,
      }),
    },
    smooth: {
      type: [Boolean, Number],
      default: 0.45,
    },
    lineWidth: {
      type: Number,
      default: 2,
    },
    legendSelectedMode: {
      type: [Boolean, String],
      default: true,
    },
  },
  setup(props) {
    const chartContainer = ref(null);
    let chartInstance = null;
    let resizeObserver = null;

    const buildSeries = () => {
      return props.seriesData.map((item, index) => {
        const color = item.color || defaultSeriesData[index % defaultSeriesData.length].color;
        const lineWidth = item.lineWidth || item.width || props.lineWidth;
        const showArea = item.showArea !== false;

        const seriesItem = {
          name: item.name,
          type: "line",
          smooth: item.smooth === undefined ? props.smooth : item.smooth,
          symbol: item.symbol || "none",
          data: item.data || item.value || [],
          itemStyle: {
            color,
          },
          lineStyle: {
            width: lineWidth,
            color,
            shadowColor: item.shadowColor || hexToRgba(color, 0.75),
            shadowBlur: item.shadowBlur === undefined ? 12 : item.shadowBlur,
          },
        };

        if (showArea) {
          seriesItem.areaStyle = {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: item.areaStartColor || hexToRgba(color, item.areaStartAlpha || 0.28) },
              { offset: 0.55, color: item.areaMiddleColor || hexToRgba(color, item.areaMiddleAlpha || 0.1) },
              { offset: 1, color: item.areaEndColor || hexToRgba(color, 0) },
            ]),
          };
        }

        return seriesItem;
      });
    };

    const buildOption = () => {
      const names = props.seriesData.map((item) => item.name);
      const colors = props.seriesData.map((item, index) => (
        item.color || defaultSeriesData[index % defaultSeriesData.length].color
      ));

      return {
        animation: false,
        color: colors,
        tooltip: {
          show: true,
          trigger: "axis",
          confine: true,
          backgroundColor: "rgba(10, 20, 54, 0.92)",
          borderColor: "rgba(80, 134, 255, 0.35)",
          textStyle: {
            color: "#dbe7ff",
            fontSize: 18,
          },
        },
        legend: {
          show: true,
          top: 0,
          right: 0,
          itemWidth: 30,
          itemHeight: 12,
          itemGap: 38,
          icon: "roundRect",
          selectedMode: props.legendSelectedMode,
          data: names,
          textStyle: {
            color: "rgba(210, 222, 255, 0.58)",
            fontSize: 22,
            fontWeight: 700,
            padding: [0, 0, 0, 8],
          },
        },
        grid: {
          left: 80,
          right: 50,
          bottom: 40,
          top: 54,
          containLabel: false,
        },
        xAxis: {
          type: "category",
          data: props.xAxisData,
          boundaryGap: false,
          axisTick: { show: false },
          axisLine: {
            lineStyle: {
              color: "rgba(102, 128, 180, 0.2)",
              width: 1,
            },
          },
          axisLabel: {
            interval: 0,
            margin: 14,
            color: "rgba(195, 209, 242, 0.5)",
            fontSize: 22,
            fontWeight: 600,
          },
        },
        yAxis: {
          type: "value",
          min: props.yAxis.min,
          max: props.yAxis.max,
          interval: props.yAxis.interval,
          axisTick: { show: false },
          axisLine: { show: false },
          splitLine: {
            lineStyle: {
              color: "rgba(151, 171, 222, 0.11)",
              width: 2,
            },
          },
          axisLabel: {
            margin: 18,
            color: "rgba(195, 209, 242, 0.58)",
            fontSize: 22,
            fontWeight: 600,
            formatter: props.yAxis.formatter || "{value}",
          },
        },
        series: buildSeries(),
      };
    };

    const setChartOption = () => {
      if (!chartInstance) return;
      chartInstance.setOption(buildOption(), true);
    };

    const resizeChart = () => {
      chartInstance && chartInstance.resize();
    };

    onMounted(async () => {
      await nextTick();
      if (!chartContainer.value) return;

      chartInstance = echarts.init(chartContainer.value);
      setChartOption();

      requestAnimationFrame(resizeChart);
      window.addEventListener("resize", resizeChart);

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(resizeChart);
        resizeObserver.observe(chartContainer.value);
      }
    });

    watch(
      () => [props.xAxisData, props.seriesData, props.yAxis, props.title, props.metricTitle, props.rangeText],
      () => {
        setChartOption();
        requestAnimationFrame(resizeChart);
      },
      { deep: true }
    );

    onUnmounted(() => {
      window.removeEventListener("resize", resizeChart);
      resizeObserver && resizeObserver.disconnect();
      chartInstance && chartInstance.dispose();
    });

    return {
      chartContainer,
    };
  },
};
</script>

<style lang="less" scoped>
.live-trend {
  width: 100%;
  height: 100%;
  min-height: 290px;
  display: flex;
  flex-direction: column;
  padding: 26px 26px 14px;
  box-sizing: border-box;
  overflow: hidden;
  background: linear-gradient(to top, rgba(10, 37, 60, 0.5), #0a253c);

  .trend-header {
    height: 52px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-shrink: 0;
    margin-bottom: 20px;

    .trend-title {
      font-size: 36px;
      line-height: 42px;
      font-weight: 600;
      color: rgba(225, 234, 255, 0.72);
      text-shadow: 0 0 8px rgba(170, 202, 255, 0.18);
    }

    .trend-select {
      height: 48px;
      min-width: 126px;
      padding: 0 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      border-radius: 24px;
      background: rgba(6, 16, 50, 0.36);
      color: rgba(210, 222, 255, 0.72);
      font-size: 22px;
      font-weight: 700;
      white-space: nowrap;
    }
  }

  .trend-body {
    min-height: 0;
    flex: 1;
    position: relative;
  }

  .legend-title {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 26px;
    line-height: 32px;
    font-weight: 700;
    color: #eef5ff;
    white-space: nowrap;

    .help-icon {
      width: 20px;
      height: 20px;
      line-height: 18px;
      text-align: center;
      border: 2px solid rgba(189, 202, 232, 0.24);
      border-radius: 50%;
      color: rgba(189, 202, 232, 0.4);
      font-size: 14px;
      font-weight: 700;
      box-sizing: border-box;
    }
  }

  .chart-container {
    width: 100%;
    height: 100%;
    min-height: 206px;
  }
}
</style>
